// =================================================================================================
// © 2021 Shrewd Apps
// ALL RIGHTS RESERVED.

import React, { Suspense, useState } from "react";
import { PropTypes } from "prop-types";
import { useLocation } from "react-router-dom";
import { Link as RoutingNavLink } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Nav, NavItem, NavLink } from "reactstrap";
import { Button } from "reactstrap";
import { Helmet } from "react-helmet";

import PageErrorBoundary from "global/errors/PageErrorBoundary";
import ErrorLoadingSection from "global/errors/ErrorLoadingSection";
import LoadingSpinner from "global/loading/LoadingSpinner";
import LinkToHomePage from "common/links/LinkToHomePage";
import TournamentsTable from "./TournamentsTable";

import { constructGetTournamentsUrl } from "api";
import { fetchResource } from "global/loading/fetchResource";
import { parseUrlSearchParameters } from "global/utilities/parseUrlSearchParameters";
import { getFormatDisplayName } from "common/resources/formatNames";
import { getCurrentDateIso8601 } from "global/utilities/Date";

// -------------------------------------------------------------------------------------------------
// Page that shows filterable list of linked tournament summaries.
// REVIEW
export default function TournamentsPage() {
	const location = useLocation();
	const urlFilterParameters = parseUrlSearchParameters(location.search);

	return (
		<PageErrorBoundary>
			<TournamentsPageContents urlFilterParameters={urlFilterParameters} />
		</PageErrorBoundary>
	);
}

// -------------------------------------------------------------------------------------------------
export function TournamentsPageContents({ urlFilterParameters, displayOptions }) {
	const apiFilterParameters = getApiFilterParameters(urlFilterParameters);
	const getTournamentsUrl = constructGetTournamentsUrl(apiFilterParameters);
	const tournamentsResource = fetchResource(getTournamentsUrl);
	const uiFilterParameters = getUIFilterParameters(urlFilterParameters);

	return (
		<Container>
			<Row>
				<Col>
					<Breadcrumbs />
				</Col>
			</Row>

			<Row>
				<Col>
					<FilterBubbles uiFilterParameters={uiFilterParameters} />
				</Col>
			</Row>

			<Suspense fallback={<LoadingSpinner />}>
				<Row>
					<Col>
						{/* FUTURE: Pass callback for adding filter or pass existing link so we can construct filter links */}
						<TournamentsTableLoader
							tournamentsResource={tournamentsResource}
							displayOptions={displayOptions}
							url={getTournamentsUrl} />
					</Col>
				</Row>
			</Suspense>
		</Container>
	);
}

TournamentsPageContents.propTypes = {
	urlFilterParameters: PropTypes.object,
	displayOptions: PropTypes.object
};

// -------------------------------------------------------------------------------------------------
function TournamentsTableLoader({ tournamentsResource, displayOptions, url }) {
	const [tournaments, setTournaments] = useState();
	const [tournamentsState, setTournamentsState] = useState();
	const [isLoadingMoreData, setIsLoadingMoreData] = useState();
	const [currentUrl, setCurrentUrl] = useState();

	if (currentUrl !== url) {
		setTournaments(tournamentsResource.data);
		setTournamentsState(tournamentsResource.state);
		setCurrentUrl(url);
	}

	if (!currentUrl) {
		return null;
	}

	const loadMoreData = async () => {
		setIsLoadingMoreData(true);
		const moreTournamentsResource = await tournamentsState.loadMoreData();
		setIsLoadingMoreData(false);

		if (url === currentUrl) {
			setTournaments(tournaments.concat(moreTournamentsResource.data));
			setTournamentsState(moreTournamentsResource.state);
		}
	};

	return (
		<>
			{tournaments &&
				<TournamentsTable tournaments={tournaments} displayOptions={displayOptions} />}
			{tournamentsState && tournamentsState.hasMoreData && !isLoadingMoreData &&
				<div className="text-center">
					<Button style={{ cursor: "pointer" }} color="link" onClick={loadMoreData}>
						show more...
					</Button>
				</div>
			}
			{isLoadingMoreData && <LoadingSpinner />}
			{tournamentsState && tournamentsState.isError &&
				<ErrorLoadingSection errorMessage={tournamentsState.errorMessage} />
			}
		</>
	);
}

TournamentsTableLoader.propTypes = {
	url: PropTypes.string,
	tournamentsResource: PropTypes.object,
	displayOptions: PropTypes.object
};

// -----------------------------------------------------------------------------
// FUTURE: Calculate link so it can keep other filters rather than clearing all
function FilterBubbles({ uiFilterParameters }) {
	return (uiFilterParameters && uiFilterParameters.length > 0 &&
		<Nav pills className="m-2">
			{uiFilterParameters.map((uiFilterParameter, index) =>
				<NavItem key={index}>
					<NavLink to="/tournaments" tag={RoutingNavLink} active>
						<FilterBubble uiFilterParameter={uiFilterParameter} />
					</NavLink>
				</NavItem>)
			}
		</Nav>
	);
}

const uiFilterParameterSchema = {
	displayName: PropTypes.string.isRequired,
	displayValue: PropTypes.string,
	internalValue: PropTypes.string
};

FilterBubbles.propTypes = {
	uiFilterParameters: PropTypes.arrayOf(PropTypes.shape(uiFilterParameterSchema))
};

// -----------------------------------------------------------------------------
function FilterBubble({ uiFilterParameter }) {
	return (
		<>
			{uiFilterParameter.displayName}: {uiFilterParameter.displayValue ? uiFilterParameter.displayValue : "<unknown>"}
			{" "}
			<span className="fas fa-times-circle" />
		</>
	);
}

FilterBubble.propTypes = {
	uiFilterParameter: PropTypes.shape(uiFilterParameterSchema)
};

// -------------------------------------------------------------------------------------------------
function getApiFilterParameters(urlFilterParameters) {
	let apiFilterParameters = {
		maxItems: 25
	};

	if (urlFilterParameters && urlFilterParameters.formats) {
		apiFilterParameters.formatId = urlFilterParameters.formats;
	}

	return apiFilterParameters;
}

// -------------------------------------------------------------------------------------------------
function getUIFilterParameters(urlFilterParameters) {
	let filterParameters = [];

	if (urlFilterParameters && urlFilterParameters.formats) {
		// FUTURE: Handle collection of formats
		filterParameters.push({
			displayName: "Format",
			displayValue: getFormatDisplayName(urlFilterParameters.formats),
			internalValue: urlFilterParameters.formats
		});
	}

	return filterParameters;
}

// -------------------------------------------------------------------------------------------------
function Breadcrumbs() {
	const rootDomain = `${window.location.protocol}//${window.location.hostname}`;
	const title = "Tournaments | Magic the Gathering | MTG Top Decks";
	const shortTitle = "Tournaments";
	const description = "List of recent Magic the Gathering tournaments";
	const date = getCurrentDateIso8601();
	const breadcrumbsLdJson = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement:": [{
			"@type": "ListItem",
			"position": 1,
			"name": "Home",
			"item": rootDomain
		},
		{
			"@type": "ListItem",
			"position": 2,
			"name": "Tournaments"
		}]
	};

	return (
		<>
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:title" content={shortTitle} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={window.location.href} />
				<meta property="og:modified_time" content={date} />
				<meta property="og:site_name" content="MTG Top Decks" />
				<script type="application/ld+json">{JSON.stringify(breadcrumbsLdJson)}</script>
			</Helmet>

			<Breadcrumb>
				<BreadcrumbItem><LinkToHomePage>Home</LinkToHomePage></BreadcrumbItem>
				<BreadcrumbItem active>Tournaments</BreadcrumbItem>
			</Breadcrumb>
		</>
	);
}